<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Building2, Users, Kanban, Activity, Layers } from 'lucide-svelte';
  import { adminStatsApi, adminApiKeysApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { formatIST } from '$lib/utils/dates';

  let requestPage = $state(1);
  const requestsQuery = createQuery(() => ({
    queryKey: ['admin-api-key-requests', requestPage],
    queryFn: () => adminApiKeysApi.requests(requestPage)
  }));

  const statsQuery = createQuery(() => ({
    queryKey: ['admin-stats'],
    queryFn: () => adminStatsApi.get()
  }));

  const KPI_DEFS = [
    { key: 'companies', label: 'Companies', icon: Building2, tint: '#E6E9EE', color: '#10223A' },
    { key: 'people', label: 'People', icon: Users, tint: '#FBF0DC', color: '#8a5c00' },
    { key: 'deals', label: 'Open deals', icon: Kanban, tint: '#E3F0F1', color: '#226A73' },
    { key: 'activities', label: 'Touchpoints', icon: Activity, tint: '#ECF8CC', color: '#5c7a00' }
  ] as const;

  function verticalColor(slug: string): string {
    const value = session.me?.verticals.find(vertical => vertical.slug === slug)?.settings?.color;
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : '#226A73';
  }

  const totals = $derived.by(() => {
    const rows = Object.values(statsQuery.data?.stats ?? {});
    return {
      companies: rows.reduce((a, r) => a + r.companies, 0),
      people: rows.reduce((a, r) => a + r.people, 0),
      deals: rows.reduce((a, r) => a + r.deals, 0),
      activities: rows.reduce((a, r) => a + r.activities, 0)
    };
  });
</script>

<div>
  <div class="mb-[22px]">
    <h2 class="tym-section-title">Stats</h2>
    <div class="tym-section-sub">Cross-vertical overview</div>
  </div>

  {#if statsQuery.isLoading}
    <p class="tym-loading-state">Loading stats&hellip;</p>
  {:else if statsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(statsQuery.error)}</p>
  {:else if statsQuery.data}
    <div class="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
      {#each KPI_DEFS as k (k.key)}
        <div class="tym-card px-[18px] pb-4 pt-[18px]">
          <span
            class="flex h-[34px] w-[34px] items-center justify-center rounded-[9px]"
            style="background:{k.tint}; color:{k.color}"
          >
            <k.icon class="h-[18px] w-[18px]" />
          </span>
          <div class="mt-3 font-heading text-[28px] font-bold leading-none tracking-tight text-navy">
            {totals[k.key]}
          </div>
          <div class="mt-1 text-[12.5px] text-dark-gray">{k.label}</div>
        </div>
      {/each}
    </div>

    <div class="mb-3 flex items-center justify-between">
      <h3 class="font-heading text-[15px] font-bold text-navy">Verticals at a glance</h3>
      <span class="text-xs text-dark-gray">{Object.keys(statsQuery.data.stats).length} verticals</span>
    </div>
    <div class="flex flex-col gap-2.5">
      {#each Object.entries(statsQuery.data.stats) as [slug, counts] (slug)}
        <div class="tym-card flex items-center gap-4 px-[18px] py-4">
          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style="background:{verticalColor(slug)}22; color:{verticalColor(slug)}"
          >
            <Layers class="h-[22px] w-[22px]" />
          </span>
          <div class="min-w-0 flex-1">
            <div class="font-heading text-[15px] font-bold capitalize text-navy">{slug}</div>
            <div class="mt-0.5 text-xs text-dark-gray">
              {counts.companies} companies &middot; {counts.people} people &middot; {counts.deals} deals
            </div>
          </div>
          <div class="text-right">
            <div class="font-heading text-lg font-bold text-navy">{counts.activities}</div>
            <div class="text-[11px] text-dark-gray">touchpoints</div>
          </div>
        </div>
      {:else}
        <p class="tym-empty-state">No data.</p>
      {/each}
    </div>
  {/if}

  <section class="mt-8 space-y-3" aria-labelledby="api-key-requests-title">
    <div class="flex items-center justify-between gap-3"><h3 id="api-key-requests-title" class="font-heading text-lg font-bold text-navy">Read-only API request log</h3><button class="btn-secondary" type="button" onclick={() => requestsQuery.refetch()}>Refresh</button></div>
    <p class="text-sm text-dark-gray">Authenticated key requests, including denied methods. Keys and request contents are never shown here.</p>
    {#if requestsQuery.isPending}
      <p class="tym-loading-state">Loading requests…</p>
    {:else if requestsQuery.isError}
      <div class="tym-error-state">{getErrorMessage(requestsQuery.error)} <button class="btn-secondary" type="button" onclick={() => requestsQuery.refetch()}>Retry</button></div>
    {:else if requestsQuery.data}
      <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white"><table class="w-full text-left text-sm"><thead><tr class="border-b"><th class="p-3">When (IST)</th><th class="p-3">Key</th><th class="p-3">Request</th><th class="p-3">Status</th><th class="p-3">Client IP</th></tr></thead><tbody>
        {#each requestsQuery.data.results as request (request.id)}
          <tr class="border-b">
            <td class="p-3">{formatIST(request.created_at)}</td>
            <td class="p-3">{request.key_name || (request.api_key_id ? `Key #${request.api_key_id}` : 'Unauthenticated')}</td>
            <td class="p-3 font-mono">{request.method} {request.path}</td>
            <td class="p-3">{request.status}</td>
            <td class="p-3">{request.client_ip}</td>
          </tr>
        {:else}
          <tr><td colspan="5" class="p-5 text-dark-gray">No API requests yet. Create a read-only key and use the documented MD endpoints.</td></tr>
        {/each}
      </tbody></table></div>
      <div class="flex items-center justify-end gap-3"><span class="text-sm">{requestsQuery.data.count} requests · Page {requestPage}</span><button class="btn-secondary" type="button" disabled={requestPage === 1} onclick={() => requestPage--}>Previous</button><button class="btn-secondary" type="button" disabled={requestPage * 25 >= requestsQuery.data.count} onclick={() => requestPage++}>Next</button></div>
    {/if}
  </section>
</div>
